/**
 * Map filter bar component
 * Horizontal filter bar for the Maps page
 * Matches Results page filter order: Search → Country → Category → Curated → Search Terms
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { Select, Input, Button, Space, Typography, Tooltip, Switch } from 'antd';
import {
  ClearOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useCategories, useSearchTerms, useCountries } from '@/hooks/usePlaces';
import { useMapFiltersStore } from '@/stores/useMapFiltersStore';
import { DEBOUNCE } from '@/utils/constants';
import type { MapTier } from '@/types/place';

const { Text } = Typography;

interface MapFilterBarProps {
  totalPlaces: number;
  visiblePlaces?: number;
  isLoading?: boolean;
  /** Current zoom tier - controls visibility of clustering options */
  tier?: MapTier;
  curatedCategoriesEnabled?: boolean;
  onCuratedCategoriesToggle?: (enabled: boolean) => void;
  hasCuratedCategories?: boolean;
  visibleCategories?: string[];
  nameBlacklistEnabled?: boolean;
  onNameBlacklistToggle?: (enabled: boolean) => void;
  hasBlacklistPatterns?: boolean;
}

export function MapFilterBar({ totalPlaces, visiblePlaces, isLoading, tier = 'world', curatedCategoriesEnabled, onCuratedCategoriesToggle, hasCuratedCategories, visibleCategories = [], nameBlacklistEnabled, onNameBlacklistToggle, hasBlacklistPatterns }: MapFilterBarProps) {
  const {
    category,
    country,
    searchQuery,
    searchTerm,
    setCategory,
    setCountry,
    setSearchQuery,
    setSearchTerm,
    clearFilters,
    hasActiveFilters,
  } = useMapFiltersStore();

  // Local state for search input to enable debouncing
  const [localSearchValue, setLocalSearchValue] = useState(searchQuery);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync local search value when store changes externally (e.g., clear)
  useEffect(() => {
    setLocalSearchValue(searchQuery);
  }, [searchQuery]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search handler
  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, DEBOUNCE.SEARCH);
  };

  // Immediate search (for Enter key)
  const handleSearchNow = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQuery(localSearchValue);
  };

  // Fetch categories
  const { data: categories = [] } = useCategories();

  // Fetch countries with counts (respects active filters except country)
  const { data: countryOptions = [] } = useCountries({
    ...(category && category.length > 0 ? { category } : {}),
    ...(searchTerm && searchTerm.length > 0 ? { searchTerm } : {}),
    ...(searchQuery ? { search: searchQuery } : {}),
    ...(hasCuratedCategories && curatedCategoriesEnabled ? { curatedCategoriesOnly: true } : {}),
  });

  // Fetch search terms (original search queries that found places)
  const { data: searchTerms = [] } = useSearchTerms();

  // Category options for select (filtered when curated is ON)
  const categoryOptions = useMemo(
    () => {
      const filteredCats = hasCuratedCategories && curatedCategoriesEnabled
        ? categories.filter((cat) => visibleCategories.includes(cat))
        : categories;
      return filteredCats.map((cat) => ({
        value: cat,
        label: cat,
      }));
    },
    [categories, hasCuratedCategories, curatedCategoriesEnabled, visibleCategories]
  );

  // Search term options for select
  const searchTermOptions = useMemo(
    () =>
      searchTerms.map((term) => ({
        value: term.searchQuery,
        label: term.remainingCount !== undefined && term.remainingCount !== term.placeCount
          ? `${term.searchQuery} (${term.remainingCount} / ${term.placeCount})`
          : `${term.searchQuery} (${term.placeCount})`,
      })),
    [searchTerms]
  );

  const showClear = hasActiveFilters();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        background: 'var(--ant-color-bg-container)',
        borderBottom: '1px solid var(--ant-color-border)',
        flexWrap: 'wrap',
      }}
    >
      {/* Search — first, matching Results page */}
      <Input.Search
        placeholder="Search places..."
        value={localSearchValue}
        onChange={(e) => handleSearchChange(e.target.value)}
        onSearch={handleSearchNow}
        onPressEnter={handleSearchNow}
        style={{ width: 220 }}
        allowClear
        loading={isLoading}
        enterButton
      />

      {/* Country filter */}
      {countryOptions.length > 0 && (
        <Select
          mode="multiple"
          placeholder="Country"
          allowClear
          value={country || []}
          onChange={setCountry}
          options={countryOptions.map((c) => ({
            value: c.country,
            label: `${c.country} (${c.count.toLocaleString()})`,
          }))}
          style={{ width: 200 }}
          showSearch
          maxTagCount={1}
          optionFilterProp="label"
        />
      )}

      {/* Category filter */}
      <Select
        mode="multiple"
        placeholder="Category"
        allowClear
        value={category || []}
        onChange={setCategory}
        options={categoryOptions}
        style={{ width: 200 }}
        showSearch
        maxTagCount={1}
        filterOption={(input, option) =>
          (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
        }
      />

      {/* Search term filter */}
      {searchTermOptions.length > 0 && (
        <Select
          mode="multiple"
          placeholder="Search term"
          allowClear
          value={searchTerm || []}
          onChange={setSearchTerm}
          options={searchTermOptions}
          style={{ width: 220 }}
          showSearch
          maxTagCount={1}
          filterOption={(input, option) =>
            (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
          }
        />
      )}

      {/* Curated categories toggle */}
      {onCuratedCategoriesToggle && (
        <Tooltip title={
          !hasCuratedCategories
            ? 'No curated categories configured (set up in Settings → Categories)'
            : curatedCategoriesEnabled
              ? 'Curated categories filter ON'
              : 'Curated categories filter OFF'
        }>
          <Space size={4} style={{
            background: curatedCategoriesEnabled && hasCuratedCategories ? 'var(--ant-color-primary-bg)' : undefined,
            padding: '4px 6px',
            borderRadius: 4,
            opacity: hasCuratedCategories ? 1 : 0.5,
          }}>
            <AppstoreOutlined style={{ color: curatedCategoriesEnabled && hasCuratedCategories ? 'var(--ant-color-primary)' : undefined, fontSize: 12 }} />
            <Switch
              size="small"
              checked={curatedCategoriesEnabled && hasCuratedCategories}
              onChange={onCuratedCategoriesToggle}
              disabled={!hasCuratedCategories}
            />
          </Space>
        </Tooltip>
      )}

      {/* Name blacklist toggle - admin only */}
      {onNameBlacklistToggle && (
        <Tooltip title={
          !hasBlacklistPatterns
            ? 'No blacklist patterns configured (set up in Settings → Name Blacklist)'
            : nameBlacklistEnabled
              ? 'Name blacklist filter ON'
              : 'Name blacklist filter OFF'
        }>
          <Space size={4} style={{
            background: nameBlacklistEnabled && hasBlacklistPatterns ? 'var(--ant-color-primary-bg)' : undefined,
            padding: '4px 6px',
            borderRadius: 4,
            opacity: hasBlacklistPatterns ? 1 : 0.5,
          }}>
            <StopOutlined style={{ color: nameBlacklistEnabled && hasBlacklistPatterns ? 'var(--ant-color-primary)' : undefined, fontSize: 12 }} />
            <Switch
              size="small"
              checked={nameBlacklistEnabled && hasBlacklistPatterns}
              onChange={onNameBlacklistToggle}
              disabled={!hasBlacklistPatterns}
            />
          </Space>
        </Tooltip>
      )}

      {/* Tier indicator */}
      <Text type="secondary" style={{ fontSize: 11 }}>
        {tier === 'world' && '🌍 World'}
        {tier === 'region' && '🏙️ Region'}
        {tier === 'street' && '📍 Street'}
      </Text>

      {/* Clear filters */}
      {showClear && (
        <Tooltip title="Clear all filters">
          <Button size="small" icon={<ClearOutlined />} onClick={clearFilters}>
            Clear
          </Button>
        </Tooltip>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Results count */}
      <Space size={8}>
        <EnvironmentOutlined style={{ color: 'var(--ant-color-primary)' }} />
        <Text type={isLoading ? 'secondary' : undefined}>
          {isLoading ? 'Loading...' : (
            visiblePlaces !== undefined && visiblePlaces !== totalPlaces
              ? `${visiblePlaces.toLocaleString()} visible of ${totalPlaces.toLocaleString()}`
              : `${totalPlaces.toLocaleString()} places`
          )}
        </Text>
      </Space>
    </div>
  );
}

export default MapFilterBar;
